import { apiClient } from './api-client';

export type ApprovalRequestTypeAppliesTo =
  | 'PROCEDURE'
  | 'FINDING'
  | 'COMMERCIAL_REVIEW'
  | 'QUOTATION'
  | 'WORK_ORDER'
  | 'SERVICE_REPORT'
  | 'GLOBAL';

export type DefaultFirstReviewer =
  'OPERATIONS' | 'MASTER_SERVICE_ENGINEER' | 'COMMERCIAL' | 'OWNER_ADMIN';

export type CustomerApprovalMode = 'NOT_REQUIRED' | 'OPTIONAL' | 'MANDATORY';

export interface ApprovalRequestType {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  description: string | null;
  requiresCommercialReview: boolean;
  allowsPartialApproval: boolean;
  isBerType: boolean;
  portalExpiryDays: number;
  requiresCustomerApproval: boolean;
  requiresOperationsFinalApproval: boolean;
  requiresConsent: boolean;
  customerCanRequestModification: boolean;
  /**
   * Phase 7 Slice 4 / D-128 (design §C.2): this kind carries a request-scoped
   * quotation prepared by Commercial on the authorization case. Q-6: `true`
   * always comes with `requiresCommercialReview = true` (server-enforced).
   * Descriptive only — it introduces no routing (D-113 R12).
   */
  requiresQuotation: boolean;
  isActive: boolean;
  appliesTo: ApprovalRequestTypeAppliesTo[];
  defaultFirstReviewer: DefaultFirstReviewer | null;
  customerApprovalMode: CustomerApprovalMode;
  allowRoutingOverride: boolean;
  sortOrder: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApprovalRequestTypeListResponse {
  items: ApprovalRequestType[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CreateApprovalRequestTypePayload {
  code: string;
  name: string;
  description?: string;
  requiresCommercialReview?: boolean;
  allowsPartialApproval?: boolean;
  isBerType?: boolean;
  portalExpiryDays?: number;
  requiresCustomerApproval?: boolean;
  requiresOperationsFinalApproval?: boolean;
  requiresConsent?: boolean;
  customerCanRequestModification?: boolean;
  /** D-128 Q-6: refused by the API when true while requiresCommercialReview is false. */
  requiresQuotation?: boolean;
  appliesTo?: ApprovalRequestTypeAppliesTo[];
  defaultFirstReviewer?: DefaultFirstReviewer | null;
  customerApprovalMode?: CustomerApprovalMode;
  allowRoutingOverride?: boolean;
  sortOrder?: number;
}

export type UpdateApprovalRequestTypePayload = Partial<CreateApprovalRequestTypePayload> & {
  isActive?: boolean;
};

export const approvalRequestTypeService = {
  list: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<ApprovalRequestTypeListResponse> =>
    apiClient
      .get<ApprovalRequestTypeListResponse>('/approval-request-types', { params })
      .then((r) => r.data),

  listAll: (): Promise<ApprovalRequestType[]> =>
    apiClient
      .get<ApprovalRequestTypeListResponse>('/approval-request-types', {
        params: { pageSize: 100 },
      })
      .then((r) => r.data.items),

  listAllActive: (): Promise<ApprovalRequestType[]> =>
    apiClient
      .get<ApprovalRequestTypeListResponse>('/approval-request-types', {
        params: { pageSize: 100 },
      })
      .then((r) => r.data.items.filter((t) => t.isActive)),

  create: (payload: CreateApprovalRequestTypePayload): Promise<ApprovalRequestType> =>
    apiClient.post<ApprovalRequestType>('/approval-request-types', payload).then((r) => r.data),

  update: (id: string, payload: UpdateApprovalRequestTypePayload): Promise<ApprovalRequestType> =>
    apiClient
      .patch<ApprovalRequestType>(`/approval-request-types/${id}`, payload)
      .then((r) => r.data),

  remove: (id: string): Promise<void> =>
    apiClient.delete(`/approval-request-types/${id}`).then(() => undefined),
};
